const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.citycourier.org/api/v1';

export interface User {
    id: number;
    name: string;
    email: string;
    loginID: string;
    type: 'ADMIN' | 'MANAGER';
    status: number;
    created_at: string;
    branch_id?: number;
    profile?: UserProfile;
    branch?: Branch;
}

export interface UserProfile {
    id: number;
    user_id: number;
    phone?: string;
    phone2?: string;
    cnic?: string;
    address?: string;
    city_id?: number;
    city?: City;
}

export interface Branch {
    id: number;
    name: string;
    code: string;
}

export interface City {
    id: number;
    name: string;
    code: string;
}

export interface CreateUserData {
    name: string;
    email: string;
    type: 'ADMIN' | 'MANAGER';
    branch_id?: number;
    password: string;
    phone?: string;
    phone2?: string;
    cnic?: string;
    address?: string;
    city_id?: number;
}

export interface UpdateUserData {
    name?: string;
    email?: string;
    type?: 'ADMIN' | 'MANAGER';
    branch_id?: number;
    password?: string;
    phone?: string;
    phone2?: string;
    cnic?: string;
    address?: string;
    city_id?: number;
}

export interface UsersListParams {
    type?: string;
    status?: string;
    query?: string;
    page?: number;
    per_page?: number;
}

export interface UsersListResponse {
    users: User[];
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    };
}

export interface ApiResponse<T> {
    success: boolean;
    data: T;
    message: string;
}

export interface BackendResponse<T> {
    success: boolean;
    data: T;
    message: string;
}

// Helper function to get auth token
const getAuthToken = (): string | null => {
    return localStorage.getItem('token');
};

// Helper function to handle API errors
const handleApiError = async (response: Response): Promise<never> => {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
};

export const userApi = {
    // Get users list with filtering and pagination
    getUsers: async (params: UsersListParams = {}): Promise<UsersListResponse> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value.toString());
            }
        });

        const response = await fetch(`${API_BASE_URL}/users?${queryParams}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<UsersListResponse> = await response.json();
        return data.data;
    },

    // Get single user details
    getUser: async (id: number): Promise<User> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/users/${id}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ user: User }> = await response.json();
        return data.data.user;
    },

    // Create new user
    createUser: async (userData: CreateUserData): Promise<User> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/users`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(userData),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ user: User }> = await response.json();
        return data.data.user;
    },

    // Update user
    updateUser: async (id: number, userData: UpdateUserData): Promise<User> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/users/${id}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(userData),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ user: User }> = await response.json();
        return data.data.user;
    },

    // Delete user
    deleteUser: async (id: number): Promise<void> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/users/${id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }
    },

    // Toggle user status (activate/deactivate)
    toggleUserStatus: async (id: number): Promise<{ id: number; name: string; status: number }> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/users/${id}/toggle-status`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ user: { id: number; name: string; status: number } }> = await response.json();
        return data.data.user;
    },

    // Get branches list
    getBranches: async (): Promise<Branch[]> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/users/branches`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ branches: Branch[] }> = await response.json();
        return data.data.branches;
    },

    // Get cities list
    getCities: async (): Promise<City[]> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/users/cities`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ cities: City[] }> = await response.json();
        return data.data.cities;
    },
}; 